import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LedgerRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { Badge } from '@/components/Badge';
import type { BadgeTone } from '@/components/Badge';
import { IconBook } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchLedger, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { subtractAmounts, sumAmounts } from '@/lib/decimal';
import { amountSign, formatMoney, formatPosDate } from '@/lib/format';

const KIND_LABEL: Record<LedgerRow['kind'], string> = {
  SALE: 'Sale',
  SALES_RETURN: 'Sales return',
  PURCHASE: 'Purchase',
  PURCHASE_RETURN: 'Purchase return',
};

const KIND_TONE: Record<LedgerRow['kind'], BadgeTone> = {
  SALE: 'success',
  SALES_RETURN: 'warning',
  PURCHASE: 'accent',
  PURCHASE_RETURN: 'warning',
};

interface LedgerLine extends LedgerRow {
  /** Debit-minus-credit carried down the rows; null if a figure was unparseable. */
  running: string | null;
}

const FILTER_FIELDS = ['Reference', 'Description', 'Type'] as const;

const TOGGLEABLE = ['description', 'debit', 'credit', 'running'] as const;

const COLUMN_LABELS: Record<string, string> = {
  description: 'Description',
  debit: 'Debit',
  credit: 'Credit',
  running: 'Running',
};

export function LedgerPage() {
  const { currency, filters } = usePortal();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);
  const toolbar = useReportToolbar();
  const layout = useReportLayout('ledger', TOGGLEABLE);

  const ledgerQuery = useQuery({
    queryKey: portalKeys.ledger(range),
    queryFn: ({ signal }) => fetchLedger(range, signal),
  });

  const reportCurrency = ledgerQuery.data?.currency || currency;

  // The toolbar's search + Filters narrow the raw rows first, and the
  // running balance is then computed over that filtered set — the whole
  // result set is already in the browser (this report isn't
  // server-paginated), so this is the client-side twin of what Orders does
  // in its API query.
  const filteredRows = useMemo(
    () =>
      applyClientFilters(ledgerQuery.data?.rows ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Reference'
            ? row.reference
            : field === 'Description'
              ? (row.description ?? undefined)
              : field === 'Type'
                ? KIND_LABEL[row.kind]
                : undefined,
        searchValues: (row) => [row.reference, row.description],
      }),
    [ledgerQuery.data, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );

  // Running balance = sum(debit) - sum(credit), computed here in exact scaled
  // integers — the API only supplies the end totals.
  const lines = useMemo<LedgerLine[]>(() => {
    let running: string | null = '0.00';
    return filteredRows.map((row) => {
      const movement = subtractAmounts(row.debit, row.credit);
      running = running === null || movement === null ? null : sumAmounts([running, movement]);
      return { ...row, running };
    });
  }, [filteredRows]);

  const baseColumns = useMemo<Array<Column<LedgerLine>>>(
    () => [
      {
        key: 'date',
        header: 'Date',
        pinned: true,
        cell: (row) => <span className="whitespace-nowrap text-slate-700">{formatPosDate(row.date)}</span>,
      },
      {
        key: 'kind',
        header: 'Type',
        pinned: true,
        cell: (row) => <Badge tone={KIND_TONE[row.kind]}>{KIND_LABEL[row.kind] ?? row.kind}</Badge>,
      },
      {
        key: 'reference',
        header: 'Reference',
        pinned: true,
        cellClassName: 'font-medium text-slate-900',
        cell: (row) => <span className="whitespace-nowrap">{row.reference || '—'}</span>,
      },
      {
        key: 'description',
        header: 'Description',
        cell: (row) => <span className="text-slate-500">{row.description || '—'}</span>,
      },
      {
        key: 'debit',
        header: 'Debit',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => (amountSign(row.debit) === 0 ? <span className="text-slate-300">—</span> : formatMoney(row.debit, reportCurrency)),
      },
      {
        key: 'credit',
        header: 'Credit',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) =>
          amountSign(row.credit) === 0 ? (
            <span className="text-slate-300">—</span>
          ) : (
            formatMoney(row.credit, reportCurrency)
          ),
      },
      {
        key: 'running',
        header: 'Running',
        align: 'right',
        cellClassName: 'tabular-nums font-medium text-slate-900',
        cell: (row) => (row.running === null ? '—' : formatMoney(row.running, reportCurrency)),
      },
    ],
    [reportCurrency],
  );

  const columns = useMemo(() => applyReportLayout(baseColumns, layout), [baseColumns, layout]);

  const header = (
    <PageHeader
      title="Ledger"
      description="Money in and out for the selected range: sales and purchase returns debit, purchases and sales returns credit."
      actions={
        <ExportMenu
          report="ledger"
          range={range}
          // Nothing to export until the report has loaded, and an
          // errored report would produce an empty file.
          disabled={ledgerQuery.isPending || ledgerQuery.isError}
        />
      }
    />
  );

  // One query feeds the tiles and the table, so a failure is reported once.
  if (ledgerQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={ledgerQuery.error}
            onRetry={() => void ledgerQuery.refetch()}
            retrying={ledgerQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={ledgerQuery}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 3 }).map((_, index) => (
                <KpiTileSkeleton key={index} />
              ))}
            </KpiGrid>
          }
        >
          {(report) => (
            <KpiGrid>
              <KpiTile label="Total debit" value={formatMoney(report.totalDebit, report.currency || currency)} />
              <KpiTile label="Total credit" value={formatMoney(report.totalCredit, report.currency || currency)} />
              <KpiTile
                label="Net"
                value={formatMoney(report.net, report.currency || currency)}
                tone={amountSign(report.net) < 0 ? 'negative' : 'positive'}
              />
            </KpiGrid>
          )}
        </AsyncSection>

        <SectionCard headingId="ledger-table" title="Movements" count={lines.length}>
          <ReportToolbar>
            <ReportSearchField
              value={toolbar.search}
              onChange={toolbar.setSearch}
              placeholder="Reference or description"
            />
            <ReportFiltersButton
              fields={FILTER_FIELDS}
              applied={toolbar.filterRows}
              matchAny={toolbar.matchAny}
              onApply={toolbar.applyFilters}
              placeholderFor={(field) => (field === 'Type' ? 'Sale / Purchase / Return' : 'value')}
            />
            <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
          </ReportToolbar>
          <AsyncSection
            state={ledgerQuery}
            skeleton={<TableSkeleton rows={8} columns={6} />}
            isEmpty={() => lines.length === 0}
            emptyIcon={<IconBook className="h-5 w-5" />}
            emptyTitle={toolbar.search ? `No movements match “${toolbar.search}”` : 'No movements in this range'}
            emptyDescription="Pick a wider date range, or check another branch."
          >
            {(report) => (
              <DataTable
                caption="Ledger movements with debit, credit and a running balance"
                columns={columns}
                rows={lines}
                rowKey={(row, index) => `${row.kind}-${row.reference}-${index}`}
                layout={layout}
                totals={{
                  label: 'Totals',
                  cells: {
                    debit: formatMoney(report.totalDebit, report.currency || currency),
                    credit: formatMoney(report.totalCredit, report.currency || currency),
                    running: formatMoney(report.net, report.currency || currency),
                  },
                }}
              />
            )}
          </AsyncSection>
        </SectionCard>
      </div>
    </>
  );
}
